import { HttpClient } from '@adapters/HttpClient';
import { UserModel } from '../models/User';

export class UsersApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Obtener todos los usuarios
   * @returns Promise con array de usuarios
   */
  async getAll(): Promise<UserModel[]> {
    const response = await this.httpClient.get<UserModel[]>('/users');
    return Array.isArray(response) ? response : response.data || [];
  }

  /**
   * Desvincular un usuario del team actual (detach)
   * Hace POST a /users/{userId}/team/detach para desvincular al usuario del team
   * @param userId - ID del usuario a desvincular
   * @returns Promise con respuesta de la API
   */
  async deleteUser(userId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.httpClient.post(`/users/${userId}/team/detach`, {});

      return {
        success: true,
        message:
          (response as any)?.message ||
          (response as any)?.data?.message ||
          'Usuario desvinculado del team exitosamente',
      };
    } catch (error: any) {
      // 🎯 Extraer mensaje de error de la estructura de la API
      let errorMessage = 'Error al desvincular el usuario del team';

      if (error.response?.data) {
        const errorData = error.response.data;

        // La API devuelve el error en errorData.error.message
        if (errorData.error && typeof errorData.error === 'object' && errorData.error.message) {
          errorMessage = errorData.error.message;
        }
        // También puede venir directamente en errorData.message
        else if (errorData.message && typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
        // O como string en errorData.error
        else if (typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        }
      } else if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * Crear un nuevo usuario
   * @param userData - Datos del usuario a crear
   * @returns Promise con respuesta de la API
   */
  async createUser(userData: {
    name: string;
    email: string;
    password: string;
    role_id: number;
    team_id: number;
  }): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.httpClient.post('/users', {
        name: userData.name,
        email: userData.email,
        password: userData.password,
        role_id: userData.role_id,
        team_id: userData.team_id,
      });

      // 🎯 Si la respuesta es exitosa (200 OK), devolver success: true
      return {
        success: true,
        message: (response as any)?.message || (response as any)?.data?.message || 'Usuario creado exitosamente',
      };
    } catch (error: any) {
      // 🎯 Extraer mensaje de error de la estructura de la API
      let errorMessage = 'Error al crear el usuario';

      if (error.response?.data) {
        const errorData = error.response.data;

        // La API devuelve el error en errorData.error.message
        if (errorData.error && typeof errorData.error === 'object' && errorData.error.message) {
          errorMessage = errorData.error.message;
        }
        // También puede venir directamente en errorData.message
        else if (errorData.message && typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
        // O como string en errorData.error
        else if (typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        }
      } else if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * Invitar un usuario a unirse al equipo
   * Crea una invitación pendiente que el usuario debe aceptar mediante un enlace enviado por correo
   * @param inviteData - Datos de la invitación (email y role_id)
   * @returns Promise con respuesta de la API
   */
  async inviteUser(inviteData: { email: string; role_id: number }): Promise<{ success: boolean; message: string }> {
    try {
      // El endpoint devuelve 204 No Content en caso de éxito
      await this.httpClient.post('/teams/users/invite', {
        email: inviteData.email,
        role_id: inviteData.role_id,
      });

      return {
        success: true,
        message: 'Invitación enviada exitosamente',
      };
    } catch (error: any) {
      // 🎯 Extraer mensaje de error de la estructura de la API
      let errorMessage = 'Error al enviar la invitación';

      if (error.response?.data) {
        const errorData = error.response.data;

        // Manejar códigos de error específicos de invitación
        if (errorData.error_code === 'USER_ALREADY_EXISTS') {
          errorMessage = errorData.message || 'El usuario ya está en el equipo';
        } else if (errorData.error_code === 'USER_ALREADY_INVITED_TO_TEAM') {
          errorMessage = errorData.message || 'El usuario ya fue invitado a este equipo';
        } else if (errorData.error_code === 'CANNOT_INVITE_USERS') {
          errorMessage = errorData.message || 'No tienes permisos para invitar usuarios';
        }
        // La API devuelve el error en errorData.error.message
        else if (errorData.error && typeof errorData.error === 'object' && errorData.error.message) {
          errorMessage = errorData.error.message;
        }
        // También puede venir directamente en errorData.message
        else if (errorData.message && typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
        // O como string en errorData.error
        else if (typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        }
        // Manejar errores de validación (422)
        else if (error.response?.status === 422 && errorData.errors) {
          const firstError = Object.values(errorData.errors)[0];
          if (Array.isArray(firstError) && firstError.length > 0) {
            errorMessage = firstError[0] as string;
          }
        }
      } else if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * Obtener información sobre una invitación
   * @param token - Token de la invitación
   * @returns Promise con información de la invitación
   */
  async getInvitationInfo(token: string): Promise<InvitationInfo> {
    const response = await this.httpClient.get<InvitationInfo>(`/teams/users/invitation/${encodeURIComponent(token)}`);
    return response;
  }

  /**
   * Aceptar una invitación de usuario al equipo
   * @param token - Token de la invitación (viene como query param en la URL)
   * @param name - Nombre del usuario
   * @param password - Contraseña del usuario
   * @returns Promise con token JWT para autenticación
   */
  async acceptInvitation(
    token: string,
    name: string = '',
    password: string = '',
  ): Promise<{
    token: string;
    type?: string;
    expires_at?: string;
  }> {
    try {
      // Usar directamente el cliente axios para poder acceder a headers en caso de 204
      const axiosClient = (this.httpClient as any).client;
      const axiosResponse = await axiosClient.post(
        `/teams/users/accept-invitation?token=${encodeURIComponent(token)}`,
        { name, password },
      );

      // Si el status es 204 o response.data es null/undefined, intentar obtener token de headers
      if (
        axiosResponse.status === 204 ||
        !axiosResponse.data ||
        (typeof axiosResponse.data === 'object' && Object.keys(axiosResponse.data).length === 0)
      ) {
        const authHeader = axiosResponse.headers?.authorization || axiosResponse.headers?.Authorization;
        if (authHeader) {
          // Formato: "Bearer <token>" o solo "<token>"
          const tokenValue = authHeader.replace(/^Bearer\s+/i, '');
          return {
            token: tokenValue,
            type: 'Bearer',
          };
        }

        // Si es 204 y no hay token en headers ni body, puede ser que el usuario ya exista
        // En este caso, la invitación fue aceptada pero el usuario necesita hacer login
        // Retornar un token especial o null para indicar que necesita login
        if (axiosResponse.status === 204) {
          throw new Error(
            'INVITATION_ACCEPTED_NO_TOKEN: La invitación fue aceptada pero el usuario necesita iniciar sesión',
          );
        }

        // Si no hay token en headers, puede ser que el backend lo devuelva en el body aunque sea 204
        // Continuar con el procesamiento normal del body
      }

      // El backend devuelve el token JWT en la respuesta
      // Puede venir en diferentes formatos según la estructura de respuesta
      const responseData = axiosResponse.data as any;

      // Si responseData es null/undefined después de verificar headers, lanzar error
      if (!responseData) {
        throw new Error(
          'INVITATION_ACCEPTED_NO_TOKEN: La invitación fue aceptada pero el usuario necesita iniciar sesión',
        );
      }

      // Formato 1: { token: { token: string, type?: string, expires_at?: string } }
      if (responseData.token && typeof responseData.token === 'object' && responseData.token.token) {
        return {
          token: responseData.token.token,
          type: responseData.token.type || 'Bearer',
          expires_at: responseData.token.expires_at,
        };
      }

      // Formato 2: { token: string }
      if (responseData.token && typeof responseData.token === 'string') {
        return {
          token: responseData.token,
          type: responseData.type || 'Bearer',
          expires_at: responseData.expires_at,
        };
      }

      // Formato 3: { data: { token: ... } }
      if (responseData.data) {
        const dataToken = responseData.data.token;
        if (dataToken) {
          if (typeof dataToken === 'object' && dataToken.token) {
            return {
              token: dataToken.token,
              type: dataToken.type || 'Bearer',
              expires_at: dataToken.expires_at,
            };
          }
          if (typeof dataToken === 'string') {
            return {
              token: dataToken,
              type: 'Bearer',
              expires_at: responseData.data.expires_at,
            };
          }
        }
      }

      throw new Error('No se recibió token en la respuesta');
    } catch (error: any) {
      // Si el error es porque response.data es null/undefined (204), intentar obtener de headers
      if (
        error.response?.status === 204 ||
        (!error.response?.data && error.response?.status >= 200 && error.response?.status < 300)
      ) {
        const authHeader = error.response?.headers?.authorization || error.response?.headers?.Authorization;
        if (authHeader) {
          const token = authHeader.replace(/^Bearer\s+/i, '');
          return {
            token,
            type: 'Bearer',
          };
        }
      }
      throw error;
    }
  }

  /**
   * Cambiar el rol de un usuario
   * @param teamId - ID del equipo
   * @param userId - ID del usuario
   * @param roleId - ID del nuevo rol
   * @returns Promise con respuesta de la API
   */
  async changeUserRole(teamId: number, userId: number, roleId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.httpClient.put(`/teams/${teamId}/users/${userId}/roles`, {
        role_id: roleId,
      });

      return {
        success: true,
        message:
          (response as any)?.message || (response as any)?.data?.message || 'Rol del usuario actualizado exitosamente',
      };
    } catch (error: any) {
      // 🎯 Extraer mensaje de error de la estructura de la API
      let errorMessage = 'Error al cambiar el rol del usuario';

      if (error.response?.data) {
        const errorData = error.response.data;

        // La API devuelve el error en errorData.error.message
        if (errorData.error && typeof errorData.error === 'object' && errorData.error.message) {
          errorMessage = errorData.error.message;
        }
        // También puede venir directamente en errorData.message
        else if (errorData.message && typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
        // O como string en errorData.error
        else if (typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        }
      } else if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * Obtener invitaciones pendientes del equipo
   * @returns Promise con array de invitaciones pendientes
   */
  async getPendingInvitations(): Promise<PendingInvitation[]> {
    const response = await this.httpClient.get<{ data: PendingInvitation[] }>('/teams/users/invitations');
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Reenviar una invitación
   * @param invitationId - ID de la invitación a reenviar
   * @returns Promise con resultado de la operación
   */
  async resendInvitation(invitationId: number): Promise<{ success: boolean; message?: string }> {
    await this.httpClient.post(`/teams/users/invitations/${invitationId}/resend`, {});
    return {
      success: true,
      message: 'Invitación reenviada exitosamente',
    };
  }

  /**
   * Cancelar una invitación
   * @param invitationId - ID de la invitación a cancelar
   * @returns Promise con resultado de la operación
   */
  async cancelInvitation(invitationId: number): Promise<{ success: boolean; message?: string }> {
    await this.httpClient.delete(`/teams/users/invitations/${invitationId}`);
    return {
      success: true,
      message: 'Invitación cancelada exitosamente',
    };
  }
}
